import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Building2, CheckCircle2, ArrowRight, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useSession } from '@/lib/useSession';
import { supabase } from '@/integrations/supabase/client';
import { useOrgStore } from '@/stores/useOrgStore';

export function OnboardingWizard() {
  const [step, setStep] = useState(0);
  const [orgName, setOrgName] = useState('');
  const [ncrNumber, setNcrNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useSession();
  const { setActiveOrg, setMemberships } = useOrgStore();

  const handleCreateOrg = async () => {
    if (!orgName.trim()) {
      toast.error('Please enter an organization name');
      return;
    }

    setLoading(true);
    try {
      // Get current session to ensure user is authenticated
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
        toast.error('Please sign in again to create an organization');
        navigate('/auth');
        return;
      }

      console.log('Creating org for user:', session.user.id);

      // Create organization
      const { data: org, error: orgError } = await supabase
        .from('organizations')
        .insert({
          name: orgName,
          ncr_number: ncrNumber || null,
        })
        .select()
        .single();

      if (orgError) throw orgError;

      // Create membership
      const { error: membershipError } = await supabase
        .from('memberships')
        .insert({
          org_id: org.id,
          user_id: session.user.id,
          role: 'owner',
        });

      if (membershipError) throw membershipError;

      // Create user role
      const { error: roleError } = await supabase
        .from('user_roles')
        .insert({
          user_id: session.user.id,
          org_id: org.id,
          role: 'owner',
        });

      if (roleError) throw roleError;

      setActiveOrg(org, 'owner');
      setMemberships([{
        org_id: org.id,
        role: 'owner',
        organization: org,
      }]);

      toast.success('Organization created successfully!');
      setStep(2);
    } catch (error: any) {
      console.error('Failed to create organization:', error);
      toast.error(error.message || 'Failed to create organization');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      title: 'Welcome to DebtNet',
      description: "Let's set up your organization",
      content: (
        <div className="space-y-4 text-center">
          <div className="flex justify-center">
            <img 
              src="/logo/debtnet-static.png" 
              alt="DebtNet logo" 
              className="h-24 w-auto"
            />
          </div>
          <p className="text-muted-foreground">
            Welcome, <strong>{user?.email}</strong>
          </p>
          <p className="text-muted-foreground">
            We'll help you set up your organization in just a few steps.
          </p>
          <Button onClick={() => setStep(1)} className="w-full">
            Get Started
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      ),
    },
    {
      title: 'Organization Details',
      description: 'Tell us about your organization',
      content: (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="orgName">Organization Name *</Label>
            <Input
              id="orgName"
              placeholder="Acme Credit Solutions"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ncrNumber">NCR Number (Optional)</Label>
            <Input
              id="ncrNumber"
              placeholder="NCRCP1234"
              value={ncrNumber}
              onChange={(e) => setNcrNumber(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep(0)} className="flex-1">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <Button onClick={handleCreateOrg} disabled={loading} className="flex-1">
              {loading ? 'Creating...' : 'Create Organization'}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      ),
    },
    {
      title: 'All Set!',
      description: 'Your organization is ready',
      content: (
        <div className="space-y-4 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', duration: 0.5 }}
            className="flex justify-center"
          >
            <div className="rounded-full bg-primary/10 p-6 border border-primary/20">
              <CheckCircle2 className="h-16 w-16 text-primary" />
            </div>
          </motion.div>
          <h3 className="text-xl font-semibold">Welcome to DebtNet!</h3>
          <p className="text-muted-foreground">
            Your organization <strong>{orgName}</strong> has been created successfully.
          </p>
          <Button onClick={() => navigate('/dashboard')} className="w-full">
            Enter Dashboard
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  const currentStep = steps[step];

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-card to-background p-4">
      <Card className="w-full max-w-lg border-border/50 shadow-lg">
        <CardHeader>
          <CardTitle>{currentStep.title}</CardTitle>
          <CardDescription>{currentStep.description}</CardDescription>
        </CardHeader>
        <CardContent>
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {currentStep.content}
            </motion.div>
          </AnimatePresence>
        </CardContent>
      </Card>
    </div>
  );
}
